import { BoxProps, Chip, ListItemText, Stack, Theme } from '@mui/material';
import { SxProps } from '@mui/system';
import Typography, { TypographyProps } from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import { forwardRef } from 'react';
import { CartItem } from '@/api/carts/carts.types.ts';
import { useTranslation } from 'react-i18next';

export function CartItemInfo(props: {
  cartItem: CartItem;
  sx?: SxProps<Theme>;
  primaryTypographyProps?: TypographyProps<'span', { component?: 'span' }>;
  secondaryTypographyProps?: TypographyProps<'p', { component?: 'p' }>;
}) {
  const { t } = useTranslation('cart');
  return (
    <ListItemText
      sx={{
        whiteSpace: {
          xs: 'normal',
          md: 'pre-line',
        },
        mr: 'auto',
        ...props.sx,
      }}
      slotProps={{
        primary: {
          ...props.primaryTypographyProps,
          onClick: () =>
            props.cartItem.item.name &&
            navigator.clipboard.writeText(props.cartItem.item.name),
        },
        secondary: {
          fontSize: '14px',
          ...props.secondaryTypographyProps,
        },
      }}
      primary={
        <Tooltip
          title={t('items.tooltips.copyDescription')}
          placement={'top-start'}
          arrow
          disableFocusListener
          disableTouchListener
          disableInteractive
        >
          <Typography
            sx={{
              fontWeight: 'medium',
              fontSize: '16px',
              cursor: props.cartItem.item.name ? 'pointer' : 'default',
            }}
          >
            {props.cartItem.item.name || t('items.labels.noDescription')}
          </Typography>
        </Tooltip>
      }
      // secondary={`Art.-Nr.: ${props.cartItem.item.itemCode}\nEAN: ${props.cartItem.item.barCode}`}
      secondary={
        <Stack
          component={'span'}
          direction={{
            xs: 'row',
            md: 'column',
          }}
          sx={{
            gap: {
              xs: 1,
              md: 0,
            },
          }}
        >
          <Tooltip
            title={t('items.tooltips.copyItemNumber')}
            arrow
            placement={'left'}
            disableFocusListener
            disableTouchListener
            disableInteractive
          >
            <span
              onClick={() =>
                navigator.clipboard.writeText(props.cartItem.item.id)
              }
              style={{
                cursor: props.cartItem.item.id ? 'pointer' : 'default',
              }}
            >
              <span
                style={{
                  fontWeight: '500',
                }}
              >
                {t('items.labels.itemNumber')}
              </span>{' '}
              {props.cartItem.item.id}
            </span>
          </Tooltip>
          <Tooltip
            title={t('items.tooltips.copyEan')}
            arrow
            placement={'left'}
            disableFocusListener
            disableTouchListener
            disableInteractive
          >
            <span
              onClick={() =>
                props.cartItem.item.barCode &&
                navigator.clipboard.writeText(props.cartItem.item.barCode)
              }
              style={{
                cursor: props.cartItem.item.barCode ? 'pointer' : 'default',
              }}
            >
              <span
                style={{
                  fontWeight: '500',
                }}
              >
                {t('items.labels.ean')}
              </span>{' '}
              {props.cartItem.item.barCode}
            </span>
          </Tooltip>
          <StockIndicatorChip cartItem={props.cartItem} />
        </Stack>
      }
    />
  );
}

function StockIndicatorChip({ cartItem }: { cartItem: CartItem }) {
  const { t } = useTranslation('cart');
  const isCto = cartItem.item.name?.endsWith(' CTO');

  if (cartItem?.item?.availableQuantity == null) {
    return;
  }
  if (cartItem.item.availableQuantity === 0 && !isCto) {
    return (
      <TooltipBox>
        <NichtAufLagerChip />
      </TooltipBox>
    );
  }
  if (cartItem.item.availableQuantity === 0 && isCto) {
    return (
      <TooltipBox>
        <BestellbarChip />
      </TooltipBox>
    );
  }
  if (cartItem.quantity > cartItem.item.availableQuantity) {
    const deliverableAmount = cartItem.item.availableQuantity;
    const notDeliverableAmount =
      cartItem.quantity - cartItem.item.availableQuantity;
    return (
      <Tooltip
        title={t('items.tooltips.stockStatus', {
          inStock: deliverableAmount,
          backorder: notDeliverableAmount,
        })}
        arrow
        placement={'left'}
        disableFocusListener
        disableTouchListener
        disableInteractive
      >
        <TooltipBox>
          <TeilweiseLieferbarChip />
        </TooltipBox>
      </Tooltip>
    );
  }
  return (
    <TooltipBox>
      <LieferbarChip />
    </TooltipBox>
  );
}

const TooltipBox = forwardRef<unknown, Omit<BoxProps, 'ref'>>((props, ref) => {
  return (
    <Box
      component={'span'}
      ref={ref}
      sx={{
        mt: 1,
        width: 'fit-content',
        ...props.sx,
      }}
      {...props}
    >
      {props.children}
    </Box>
  );
});

export default TooltipBox;

function LieferbarChip() {
  const { t } = useTranslation('cart');
  return (
    <Chip
      size={'small'}
      variant={'outlined'}
      label={t('items.stock.available')}
      color={'success'}
    />
  );
}

function TeilweiseLieferbarChip() {
  const { t } = useTranslation('cart');
  return (
    <Chip
      size={'small'}
      variant={'outlined'}
      label={t('items.stock.partiallyAvailable')}
      color={'info'}
    />
  );
}

function NichtAufLagerChip() {
  const { t } = useTranslation('cart');
  return (
    <Chip
      component={'span'}
      size={'small'}
      variant={'outlined'}
      label={t('items.stock.notInStock')}
      color={'warning'}
    />
  );
}

function BestellbarChip() {
  const { t } = useTranslation('cart');
  return (
    <Chip
      size={'small'}
      variant={'outlined'}
      label={t('items.stock.canBeOrdered')}
      color={'info'}
    />
  );
}
